import { FC, JSX, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ProfileMenu } from '../ProfileMenu/ProfileMenu';
import { ThemeSwitch } from '../ThemeSwitch/ThemeSwitch';
import './LogoHeader.scss';

export type LogoHeaderProps = {
  /** Primary wordmark image source; clicking it navigates to `/`. */
  Logo: string;
  /** Secondary mark shown to the left of the wordmark; hidden below 900px. */
  subLogo?: string;
  onLogout: () => void;
  onProfileClick?: () => void;
  /** Slot for the app's department/organization selector, rendered first on the right. */
  departmentSelectSlot?: ReactNode;
  /** Slot for the app's notification centre, rendered after the department selector. */
  notificationSlot?: ReactNode;
  /** Slot centred in the header, independent of how wide the two sides run. */
  centerSlot?: ReactNode;
  /** Role summary shown inside the profile menu; omit when no role is selected. */
  selectedRole?: {
    department: string;
    role: string;
    jobRole?: string;
  };
  profilePicture?: string;
};

export const LogoHeader: FC<LogoHeaderProps> = ({
  Logo,
  subLogo,
  onLogout,
  onProfileClick,
  departmentSelectSlot,
  notificationSlot,
  centerSlot,
  selectedRole,
  profilePicture,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLogoKeyDown = (event: KeyboardEvent<HTMLImageElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLogoClick();
    }
  };

  return (
    <div className="consolidocs-header">
      <div className="consoli-sublogo-container">
        {subLogo && (
          <img src={subLogo} className="consoli-sublogo" alt={t('Sub Logo')} />
        )}
        <img
          src={Logo}
          className="consoli-logo"
          onClick={handleLogoClick}
          onKeyDown={handleLogoKeyDown}
          role="button"
          tabIndex={0}
          alt={t('Logo')}
        />
      </div>

      {centerSlot && <div className="header-center">{centerSlot}</div>}

      <span className="header-right">
        {departmentSelectSlot}
        {notificationSlot}
        <ThemeSwitch />
        <div className="logout-btn">
          <ProfileMenu
            onLogout={onLogout}
            onProfileClick={onProfileClick}
            selectedRole={selectedRole}
            profilePicture={profilePicture}
          />
        </div>
      </span>
    </div>
  );
};
